import React, { useRef, useState } from "react";
import { message } from "antd";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUploadOrganizationDocument } from "../api/organization";
import { AppButton } from "../components/ui/AppButton";

import { Select } from "../components/ui/select";
import { FormLabel } from "../components/ui/form";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Icons } from "../assets/images/icons";

const documentTypes = [
  { value: "Business License", label: "Business License" },
  { value: "TIN Certificate", label: "TIN Certificate" },
  { value: "Other", label: "Other" },
];

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_FILE_TYPES = ["jpg", "jpeg", "png", "pdf"];

const uploadDocumentSchema = z
  .object({
    documentType: z.string().min(1, "Please select a document type"),
    customDocumentType: z.string().optional(),
    file: z
      .any()
      .refine((file) => file instanceof File, "Please upload a valid file")
      .refine(
        (file) => {
          if (!(file instanceof File)) return false;
          const ext = file.name.split(".").pop()?.toLowerCase();
          return ALLOWED_FILE_TYPES.includes(ext!);
        },
        { message: "Only JPG, JPEG, PNG, and PDF files are allowed." }
      )
      .refine(
        (file) => {
          if (!(file instanceof File)) return false;
          const sizeMB = file.size / (1024 * 1024);
          return sizeMB <= MAX_FILE_SIZE_MB;
        },
        {
          message: `File size must be less than or equal to ${MAX_FILE_SIZE_MB} MB.`,
        }
      ),
  })
  .refine(
    (data) => !(data.documentType === "Other" && !data.customDocumentType),
    {
      message: "Please specify the document type",
      path: ["customDocumentType"],
    }
  );

type UploadDocumentFormData = z.infer<typeof uploadDocumentSchema>;

type UploadDocumentModalProps = {
  open: boolean;
  hideModal: () => void;
  mode?: "create";
  // onSubmit: (values: any) => Promise<void> | void;
  // onOpenChange: (open: boolean) => void;
};

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  open,
  hideModal,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: uploadDocument, isPending: uploading } =
    useUploadOrganizationDocument();

  const [filePreview, setFilePreview] = useState<string>("");

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<UploadDocumentFormData>({
    resolver: zodResolver(uploadDocumentSchema),
    mode: "onChange",
  });

  const documentType = watch("documentType");
  const file = watch("file");

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setValue("file", selectedFile, { shouldValidate: true });

    // Preview for image types
    if (selectedFile.name.match(/\.(jpg|jpeg|png)$/i)) {
      setFilePreview(URL.createObjectURL(selectedFile));
    } else {
      setFilePreview("");
    }
  };

  const onSubmit = async (data: UploadDocumentFormData) => {
    try {
      const finalDocumentName =
        data.documentType === "Other"
          ? data.customDocumentType!
          : data.documentType;

      await uploadDocument({
        name: finalDocumentName,
        file: data.file,
        is_back: false,
      });

      message.success("Document uploaded successfully!");

      // Reset all form fields including Select
      reset({
        documentType: "", // <-- clear Select
        customDocumentType: "",
        file: undefined,
      });

      setFilePreview("");
      hideModal();
    } catch (err) {
      console.error(err);
      message.error("Upload failed. Please try again.");
    }
  };

  const handleCancel = () => {
    reset();
    setFilePreview("");
    hideModal();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-5xl bg-white p-8 !rounded-[30px] z-50">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Dialog Header */}
          <DialogHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-4">
            <DialogTitle className="text-xl font-semibold text-[#09090B]">
              Upload Document
            </DialogTitle>
            <p className="text-sm text-[#505050] pt-1">
              Select a document type and upload your document file
            </p>

            {/* Right side: Buttons */}
            <div className="flex items-center gap-2">
              <AppButton
                type="button"
                variant="outline"
                className="px-6"
                onClick={hideModal} // Use onOpenChange directly
              >
                Cancel
              </AppButton>
              <AppButton
                variant="success"
                type="submit"
                className="px-6 font-medium"
                isLoading={uploading}
              >
                Upload
              </AppButton>
            </div>
          </DialogHeader>

          {/* Document Type */}
          <div className="space-y-1">
            <Controller
              name="documentType"
              control={control}
              render={({ field }) => (
                <div>
                  <FormLabel className="block text-sm font-medium text-gray-700">
                    Document Type
                  </FormLabel>
                  <Select
                    placeholder="Select Document Type"
                    options={documentTypes.map((doc: any) => ({
                      label: doc.label,
                      value: doc.value,
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                  {errors.documentType?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.documentType.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          {documentType === "Other" && (
            <Controller
              control={control}
              name="customDocumentType"
              render={({ field, fieldState: { error } }) => (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Specify Document Type{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="Enter document type"
                    className="w-full"
                  />
                  {error && (
                    <p className="text-red-500 text-xs mt-1">{error.message}</p>
                  )}
                </div>
              )}
            />
          )}

          {/* File Upload */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Upload <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
              />

              {!file ? (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <img
                      src={Icons.uploadIcon}
                      alt="Upload"
                      className="w-12 h-12 text-gray-400"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Allowed: JPG, JPEG, PNG, PDF (max {MAX_FILE_SIZE_MB}MB)
                  </p>
                  <AppButton
                    variant="outline"
                    onClick={triggerFileInput}
                    customIcon={Icons.exportIcon as any}
                  >
                    Choose File
                  </AppButton>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <img
                      src={Icons.uploadIcon}
                      alt="File"
                      className="w-8 h-8 text-green-500"
                    />
                  </div>
                  <p className="text-sm font-medium">{file.name}</p>
                  {filePreview && (
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="max-h-32 mx-auto rounded border"
                    />
                  )}
                  <div className="flex justify-center gap-2">
                    <AppButton
                      variant="outline"
                      onClick={triggerFileInput}
                      size="sm"
                      style={{ borderRadius: "6px" }}
                    >
                      Change File
                    </AppButton>
                    <AppButton
                      variant="ghost"
                      onClick={() => {
                        setValue("file", null);
                        setFilePreview("");
                      }}
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </AppButton>
                  </div>
                </div>
              )}
            </div>
            {(() => {
              const fileErrorMessage =
                typeof errors.file === "string"
                  ? errors.file
                  : (errors.file as { message?: string })?.message;
              return fileErrorMessage ? (
                <p className="text-red-500 text-xs mt-1">{fileErrorMessage}</p>
              ) : null;
            })()}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentModal;
